import React, { useState } from 'react';
import {
  Box,
  Modal,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Email as EmailIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

export interface GuestCheckoutFormData {
  email: string;
  firstName: string;
  lastName: string;
}

interface GuestCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: GuestCheckoutFormData) => void | Promise<void>;
  onSwitchToLogin: () => void;
  isLoading?: boolean;
  error?: string | null;
  /** For free events: "Complete registration"; for paid: "Continue to payment" */
  isFreeEvent?: boolean;
}

const textFieldSx = {
  mb: { xs: 1, sm: 1.5 },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    height: '56px',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.8)' },
  },
};

const GuestCheckoutModal: React.FC<GuestCheckoutModalProps> = ({
  open,
  onClose,
  onSubmit,
  onSwitchToLogin,
  isLoading = false,
  error = null,
  isFreeEvent = false,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<GuestCheckoutFormData>({
    email: '',
    firstName: '',
    lastName: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) return;
    await onSubmit({
      email: trimmedEmail,
      firstName: formData.firstName.trim().slice(0, 255),
      lastName: formData.lastName.trim().slice(0, 255),
    });
  };

  const handleClose = () => {
    setFormData({ email: '', firstName: '', lastName: '' });
    onClose();
  };

  const isValid = formData.email.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="guest-checkout-modal-title"
      aria-describedby="guest-checkout-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        p: 0,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: '100%' },
          height: { xs: 'auto', sm: '75vh', md: '75vh', lg: '75vh', xl: '75vh' },
          maxHeight: { xs: '90vh', sm: 'none', md: 'none', lg: 'none', xl: 'none' },
          overflow: { xs: 'visible', sm: 'auto', md: 'auto', lg: 'auto', xl: 'auto' },
          borderRadius: 0,
          position: 'relative',
          bgcolor: '#5F3C6F',
          boxShadow: theme.shadows[24],
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            width: 32,
            height: 32,
            borderRadius: 0,
            zIndex: 1,
            '&:hover': {
              color: '#FF9DE7',
              backgroundColor: 'transparent',
            },
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2, sm: 2 },
            pt: { xs: 3, sm: 3.5 },
            maxWidth: { xs: '90%', sm: '100%', md: 500, lg: 500, xl: 500 },
            mx: 'auto',
          }}
        >
          <Typography
            id="guest-checkout-modal-title"
            variant="h6"
            component="h2"
            sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
          >
            Complete your booking
          </Typography>
          <Typography
            id="guest-checkout-modal-description"
            variant="body2"
            sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
          >
            Enter your email to receive the ticket confirmation. You don&apos;t need an account.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 } }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            placeholder="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            margin="normal"
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            placeholder="First name (optional)"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            margin="normal"
            disabled={isLoading}
            inputProps={{ maxLength: 255 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            placeholder="Last name (optional)"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            margin="normal"
            disabled={isLoading}
            inputProps={{ maxLength: 255 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={textFieldSx}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid || isLoading}
            sx={{
              py: 1.2,
              mt: { xs: 2, sm: 3 },
              mb: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 'medium',
              textTransform: 'none',
              backgroundColor: '#FFD700',
              color: '#000',
              '&:hover': {
                backgroundColor: '#FFC700',
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#9E9E9E',
              },
            }}
          >
            {isLoading ? 'Redirecting…' : isFreeEvent ? 'Complete registration' : 'Continue to payment'}
          </Button>

          <Box sx={{ textAlign: 'center', pb: { xs: 1.5, sm: 2 } }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                handleClose();
                onSwitchToLogin();
              }}
              sx={{
                color: '#FFD700',
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#FFC700',
                },
              }}
            >
              Already have an account? Log in
            </Link>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default GuestCheckoutModal;
